import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import type {
  CreateImChannelProactivityExperimentInput,
  CreateProactivenessRolloutInput,
  ImChannelProactivityExperimentCohort,
  ImChannelProactivityExperiment,
  ProactivenessCohortChannelFilter,
  ProactivenessFilter,
  ProactivenessRollout,
  ProactivenessSection,
  SendImChannelProactivityExperimentNowResult,
  SyncImChannelProactivityExperimentCohortResult,
  UpdateImChannelProactivityExperimentInput,
} from '@/features/im-channel-proactivity-experiments/im-channel-proactivity-experiments-types';

const COHORT_PAGE_SIZE = 10;
const QUERY_KEY = ['im-channel-proactivity-experiments'] as const;
const ROLLOUTS_QUERY_KEY = ['proactiveness-rollouts'] as const;
const sectionQuery = (section: string) =>
  section === 'experimental' ? '' : `?section=${encodeURIComponent(section)}`;
const cohortQueryKey = (section: ProactivenessFilter) =>
  [...QUERY_KEY, section, 'cohort'] as const;
const getSections = (section: ProactivenessFilter): ProactivenessSection[] =>
  section === 'all' ? ['production', 'experimental'] : [section];
const cohortQuery = (
  section: ProactivenessSection,
  page: number,
  channel: ProactivenessCohortChannelFilter,
  flagKey: string
) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(COHORT_PAGE_SIZE),
  });
  if (section !== 'experimental') params.set('section', section);
  if (channel !== 'all') params.set('channel', channel);
  if (flagKey) params.set('flag_key', flagKey);
  return `?${params.toString()}`;
};

export type CohortPages = Record<ProactivenessSection, number>;
export type CohortChannelFilters = Record<
  ProactivenessSection,
  ProactivenessCohortChannelFilter
>;
export type CohortFlagFilters = Record<ProactivenessSection, string>;

type ApiErrorBody = {
  detail?: string | { user_error_message?: string } | { msg?: string }[];
};

const apiErrorMessage = (body: ApiErrorBody | null, fallback: string) => {
  const detail = body?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail[0]?.msg ?? fallback;
  return detail?.user_error_message ?? fallback;
};

const useExperimentApi = () => {
  const { getAccessTokenSilently } = useAuth0();

  return async <T>(
    path: string,
    fallbackError: string,
    init?: RequestInit
  ): Promise<T> => {
    const token = await getAccessTokenSilently();
    const response = await apiFetch(
      `${API_URL}/admin/im-channel-proactivity-experiments${path}`,
      {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(init?.body && { 'Content-Type': 'application/json' }),
        },
      }
    );
    if (!response.ok) {
      const body = (await response
        .json()
        .catch(() => null)) as ApiErrorBody | null;
      throw new Error(apiErrorMessage(body, fallbackError));
    }
    return response.json();
  };
};

export const useProactivenessRollouts = () => {
  const request = useExperimentApi();
  return useQuery({
    queryKey: ROLLOUTS_QUERY_KEY,
    queryFn: () =>
      request<ProactivenessRollout[]>('/rollouts', 'Failed to load rollouts'),
  });
};

export const useCreateProactivenessRollout = () => {
  const request = useExperimentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProactivenessRolloutInput) =>
      request<ProactivenessRollout>('/rollouts', 'Failed to create rollout', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ROLLOUTS_QUERY_KEY }),
  });
};

export const useImChannelProactivityExperiments = (
  section: ProactivenessFilter
) => {
  const request = useExperimentApi();
  return useQuery({
    queryKey: [...QUERY_KEY, section],
    queryFn: async () => {
      const results = await Promise.all(
        getSections(section).map((selectedSection) =>
          request<ImChannelProactivityExperiment[]>(
            sectionQuery(selectedSection),
            'Failed to load proactive routines'
          )
        )
      );
      return results.flat();
    },
  });
};

export const useCreateImChannelProactivityExperiment = () => {
  const request = useExperimentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateImChannelProactivityExperimentInput) =>
      request<ImChannelProactivityExperiment>('', 'Failed to create routine', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useUpdateImChannelProactivityExperiment = () => {
  const request = useExperimentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      slug,
      section,
      input,
    }: {
      slug: string;
      section: string;
      input: UpdateImChannelProactivityExperimentInput;
    }) =>
      request<ImChannelProactivityExperiment>(
        `/${slug}${sectionQuery(section)}`,
        'Failed to update routine',
        { method: 'PUT', body: JSON.stringify(input) }
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useSyncImChannelProactivityExperimentCohort = (
  section: ProactivenessFilter
) => {
  const request = useExperimentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      for (const selectedSection of getSections(section)) {
        await request<SyncImChannelProactivityExperimentCohortResult>(
          `/sync-cohort${sectionQuery(selectedSection)}`,
          'Failed to sync the PostHog cohort',
          { method: 'POST' }
        );
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useImChannelProactivityExperimentCohort = (
  section: ProactivenessFilter,
  pages: CohortPages,
  channelFilters: CohortChannelFilters,
  flagFilters: CohortFlagFilters
) => {
  const request = useExperimentApi();
  return useQuery({
    queryKey: [...cohortQueryKey(section), pages, channelFilters, flagFilters],
    placeholderData: (previous) => previous,
    queryFn: () =>
      Promise.all(
        getSections(section).map(async (selectedSection) => ({
          section: selectedSection,
          ...(await request<ImChannelProactivityExperimentCohort>(
            `/cohort${cohortQuery(
              selectedSection,
              pages[selectedSection],
              channelFilters[selectedSection],
              flagFilters[selectedSection]
            )}`,
            'Failed to load the PostHog cohort'
          )),
        }))
      ),
  });
};

export const useSendImChannelProactivityExperimentNow = () => {
  const request = useExperimentApi();
  return useMutation({
    mutationFn: (slug: string) =>
      request<SendImChannelProactivityExperimentNowResult>(
        `/${slug}/send-now`,
        'Failed to send the experiment',
        { method: 'POST' }
      ),
  });
};

export const useSetImChannelProactivityExperimentPaused = () => {
  const request = useExperimentApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      slug,
      section,
      isPaused,
    }: {
      slug: string;
      section: string;
      isPaused: boolean;
    }) =>
      request<ImChannelProactivityExperiment>(
        `/${slug}/paused${sectionQuery(section)}`,
        `Failed to ${isPaused ? 'pause' : 'resume'} the routine`,
        { method: 'PUT', body: JSON.stringify({ is_paused: isPaused }) }
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};
